// src/pages/CustomDatingView.js
import React, { useState, useContext, useEffect, useRef } from 'react';
import { Box, Typography, AppBar, Toolbar, Button, CircularProgress, Alert, Grid, IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import { DragDropContext } from '@hello-pangea/dnd';
import axios from 'axios';

// --- IMPORTS ---
import { useTheme } from '@mui/material/styles';
import { ThemeModeContext } from '../theme/themeContext';
import { AuthContext } from '../auth/AuthContext';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import AuthComponent from '../components/AuthComponent';
import AboutComponent, { LimitationsComponent } from '../components/AboutComponent';
import CustomAppControlComponent from '../components/CustomAppControlComponent';
import FooterComponent from '../components/FooterComponent';
import ImageGallery from '../components/ImageGallery';
import ProfileComponent from '../components/ProfileComponent';

import { useFileHandler } from '../hooks/useFileHandler';
import ImageDropzone from '../components/ImageDropzone';
import FaceCluster from '../components/FaceCluster';

const API_URL = '/api'; //'http://localhost:8000';

const CustomDatingView = () => {
    // --- State ---
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [datingResultsForGallery, setDatingResultsForGallery] = useState(null);
    const [selectedFaceId, setSelectedFaceId] = useState(null);
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const [clusters, setClusters] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [message, setMessage] = useState(null);
    const [usePublicIdentities, setUsePublicIdentities] = useState(true);
    const [processedFaceIds, setProcessedFaceIds] = useState([]);
    const [useSavedIdentities, setUseSavedIdentities] = useState(false);

    // --- Hooks & Context ---
    const { authState, login, logout } = useContext(AuthContext);
    const themeMode = useContext(ThemeModeContext);
    const theme = useTheme();
    const { selectedFiles, imageUrls, handleFileUpload, handleRemoveImage, resetFiles } = useFileHandler(setMessage);

    const mainContentRef = useRef(null);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

    useEffect(() => {
        const updateSize = () => {
            if (mainContentRef.current) {
                const padding = 40;
                setContainerSize({ width: mainContentRef.current.clientWidth - padding, height: mainContentRef.current.clientHeight - padding });
            }
        };
        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, [datingResultsForGallery]);

    // --- HANDLERS ---
    const handleLogin = async (e) => { e.preventDefault(); try { const formData = new URLSearchParams(); formData.append('username', username); formData.append('password', password); const response = await axios.post(`${API_URL}/token`, formData); const newToken = response.data.access_token; login({ username: username }, newToken); axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`; setPassword(''); setMessage({ text: 'Logged in successfully!', severity: 'success' }); } catch (error) { setMessage({ text: `Login failed: ${error.response?.data?.detail || 'Unknown error'}`, severity: 'error' }); } };
    const handleLogout = () => { logout(); axios.defaults.headers.common['Authorization'] = null; setUsername(''); handleReset(); setMessage({ text: 'You have been logged out.', severity: 'info' }); };
    const onFilesAdded = (files) => { handleFileUpload(files); setClusters([]); setDatingResultsForGallery(null); };

    // --- THIS IS THE FINAL, CORRECTED HANDLEPROCESS FUNCTION ---
    const handleProcess = async () => {
        if (selectedFiles.length === 0) {
            setMessage({ text: 'Please upload images first.', severity: 'warning' });
            return;
        }
        setIsProcessing(true);
        setMessage({ text: 'Uploading and clustering faces...', severity: 'info' });

        const formData = new FormData();
        selectedFiles.forEach(file => formData.append('files', file));

        formData.append('use_saved_identities', useSavedIdentities);
        formData.append('use_public_identities', usePublicIdentities);

        try {
            // The backend now does all the heavy lifting of merging clusters.
            const response = await axios.post(`${API_URL}/cluster-faces/`, formData);
            const { clusters: apiClusters, unclustered_faces: unclustered } = response.data;

            // The backend returns a single, correct list of clusters. We just need to display it.
            const enhancedClusters = apiClusters.map(c => ({ ...c, birth_year: c.birth_year || '' }));

            if (unclustered && unclustered.length > 0) {
                const existingUnassigned = enhancedClusters.find(c => c.id === 'cluster_unassigned');
                if (existingUnassigned) {
                    existingUnassigned.faces.push(...unclustered);
                } else {
                    enhancedClusters.push({ id: 'cluster_unassigned', name: 'Unassigned Faces', faces: unclustered, birth_year: '' });
                }
            }

            setClusters(enhancedClusters);

            // We still track the new face IDs so they can be cleared from the temporary store on reset.
            const newFaceIds = (unclustered || []).map(f => f.id);
            apiClusters.forEach(c => {
                // A 'new_cluster' ID tells us its faces are temporary
                if (String(c.id).startsWith('new_cluster')) {
                    c.faces.forEach(f => newFaceIds.push(f.id));
                }
            });
            setProcessedFaceIds(newFaceIds);

            setMessage({ text: 'Clustering complete. You can now organize the identities.', severity: 'success' });

        } catch (error) {
            const errorMsg = error.response?.data?.detail || error.message || 'An unknown error occurred.';
            setMessage({ text: `Processing failed: ${errorMsg}`, severity: 'error' });
            setClusters([]);
        } finally {
            setIsProcessing(false);
        }
    };

    // The rest of the functions are correct and remain unchanged.
    const handleBirthYearChange = (clusterId, year) => { setClusters(prevClusters => prevClusters.map(c => c.id === clusterId ? { ...c, birth_year: year } : c)); };
    const onDragEnd = (result) => {
        const { source, destination, draggableId } = result;
        if (!destination || (source.droppableId === destination.droppableId && source.index === destination.index)) return;
        const faceId = draggableId;
        const newClusters = JSON.parse(JSON.stringify(clusters));
        const sourceCluster = newClusters.find(c => String(c.id) === source.droppableId);
        const destCluster = newClusters.find(c => String(c.id) === destination.droppableId);
        if (!sourceCluster || !destCluster) return;
        const faceIdKey = "face_id"; const altFaceIdKey = "id";
        const faceIndex = sourceCluster.faces.findIndex(f => f[faceIdKey] === faceId || f[altFaceIdKey] === faceId);
        if (faceIndex === -1) return;
        const [movedFace] = sourceCluster.faces.splice(faceIndex, 1);
        destCluster.faces.splice(destination.index, 0, movedFace);
        setClusters(newClusters);
    };
    const handleRemoveFace = (faceIdToRemove) => {
        const newClusters = JSON.parse(JSON.stringify(clusters));
        let faceToMove = null;
        const faceIdKey = "face_id"; const altFaceIdKey = "id";
        for (const cluster of newClusters) {
            const faceIndex = cluster.faces.findIndex(f => f[faceIdKey] === faceIdToRemove || f[altFaceIdKey] === faceIdToRemove);
            if (faceIndex !== -1) { [faceToMove] = cluster.faces.splice(faceIndex, 1); break; }
        }
        if (!faceToMove) return;
        let unassignedCluster = newClusters.find(c => c.id === 'cluster_unassigned');
        if (!unassignedCluster) { unassignedCluster = { id: 'cluster_unassigned', name: 'Unassigned Faces', faces: [], birth_year: '' }; newClusters.push(unassignedCluster); }
        unassignedCluster.faces.push(faceToMove);
        setClusters(newClusters);
    };
    const handleReset = async () => {
        if (processedFaceIds.length > 0) {
            try { await axios.post(`${API_URL}/clear-session-faces/`, processedFaceIds); }
            catch (error) { console.error("Failed to clear backend session:", error); }
        }
        resetFiles(); setClusters([]); setMessage(null); setDatingResultsForGallery(null); setSelectedFaceId(null); setCurrentSlideIndex(0); setProcessedFaceIds([]);
    };
    const handleCreateNewCluster = () => {
        let maxIdNum = 0;
        clusters.forEach(cluster => {
            const match = cluster.name.match(/^(New Identity|Identity) (\d+)$/);
            if (match && match[2]) { const num = parseInt(match[2], 10); if (num > maxIdNum) maxIdNum = num; }
        });
        const newIdNumber = maxIdNum + 1;
        const newCluster = { id: `new_cluster_${Date.now()}`, name: `New Identity ${newIdNumber}`, faces: [], birth_year: '' };
        setClusters(prevClusters => [...prevClusters, newCluster]);
        setDatingResultsForGallery(null);
    };
    const handleRemoveCluster = (clusterIdToRemove) => {
        const newClusters = JSON.parse(JSON.stringify(clusters));
        const clusterIndex = newClusters.findIndex(c => c.id === clusterIdToRemove);
        if (clusterIndex === -1) return;
        const [removedCluster] = newClusters.splice(clusterIndex, 1);
        const facesToMove = removedCluster.faces;
        if (facesToMove.length > 0) {
            let unassignedCluster = newClusters.find(c => c.id === 'cluster_unassigned');
            if (!unassignedCluster) { unassignedCluster = { id: 'cluster_unassigned', name: 'Unassigned Faces', faces: [], birth_year: '' }; newClusters.push(unassignedCluster); }
            unassignedCluster.faces.push(...facesToMove);
        }
        setClusters(newClusters);
    };
    const handleSaveIdentities = async () => {
        setIsProcessing(true); setMessage({ text: 'Saving identities to your profile...', severity: 'info' });
        const identitiesToSave = clusters.filter(c => c.id !== 'cluster_unassigned' && c.birth_year && c.faces.length > 0)
            .map(c => ({ name: c.name, birth_year: parseInt(c.birth_year, 10), face_ids: c.faces.map(f => f.face_id || f.id) }));
        if (identitiesToSave.length === 0) { setMessage({ text: 'No complete identities to save. Please provide a birth year.', severity: 'warning' }); setIsProcessing(false); return; }
        try {
            await axios.post(`${API_URL}/identities/save`, { identities: identitiesToSave });
            setMessage({ text: 'Identities saved successfully!', severity: 'success' });
        } catch (error) {
            const errorMsg = error.response?.data?.detail || 'An error occurred while saving.';
            setMessage({ text: `Save failed: ${errorMsg}`, severity: 'error' });
        } finally { setIsProcessing(false); }
    };


    const handleRunDating = async () => {
        setIsProcessing(true);
        setMessage({ text: 'Estimating dates based on identities...', severity: 'info' });
        setDatingResultsForGallery(null);

        // --- NEW LOGIC STARTS HERE ---

        // 1. Separate clusters with and without a valid birth year.
        const clustersWithBirthYear = clusters.filter(c =>
            c.id !== 'cluster_unassigned' &&
            c.faces.length > 0 &&
            c.birth_year && // Check that birth_year is not empty
            !isNaN(parseInt(c.birth_year, 10)) // Check that it's a valid number
        );

        const clustersWithoutBirthYear = clusters.filter(c =>
            c.id !== 'cluster_unassigned' &&
            c.faces.length > 0 &&
            (!c.birth_year || isNaN(parseInt(c.birth_year, 10)))
        );

        // 2. Prepare the 'identities' payload from clusters that have a birth year.
        const identitiesPayload = clustersWithBirthYear.map(c => ({
            name: c.name,
            birth_year: c.birth_year, // Already validated as a number-like string
            face_ids: c.faces.map(f => f.face_id || f.id)
        }));

        // 3. Prepare the 'unassigned_face_ids' payload.
        // Start with the faces from the actual "Unassigned" cluster.
        const originalUnassignedFaces = clusters.find(c => c.id === 'cluster_unassigned')
            ?.faces.map(f => f.face_id || f.id) || [];

        // Extract all face IDs from the clusters that were missing a birth year.
        const facesFromEmptyClusters = clustersWithoutBirthYear.flatMap(c =>
            c.faces.map(f => f.face_id || f.id)
        );

        // Combine them into the final list.
        const unassignedPayload = [...originalUnassignedFaces, ...facesFromEmptyClusters];

        // --- END OF NEW LOGIC ---

        const payload = {
            identities: identitiesPayload,
            unassigned_face_ids: unassignedPayload,
            use_public_identities: usePublicIdentities
        };

        try {
            const response = await axios.post(`${API_URL}/custom-dating/`, payload);
            const resultsByFilename = response.data.dating_results_by_image;
            const formattedResults = selectedFiles.map(file => resultsByFilename[file.name] || { filename: file.name, faces: [], predicted_year: null });
            setDatingResultsForGallery(formattedResults);
            setMessage({ text: 'Dating estimation complete!', severity: 'success' });
        } catch (error) {
            const errorMsg = error.response?.data?.detail || error.message || 'An unknown error occurred.';
            setMessage({ text: `Dating failed: ${errorMsg}`, severity: 'error' });
            setDatingResultsForGallery(null);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleRunDatingExperimental = async () => {
        setIsProcessing(true);
        setMessage({ text: 'Estimating dates with experimental model...', severity: 'info' });
        setDatingResultsForGallery(null);

        // --- PAYLOAD LOGIC STARTS HERE ---

        // 1. Separate clusters with a valid birth year.
        const clustersWithBirthYear = clusters.filter(c =>
            c.id !== 'cluster_unassigned' &&
            c.faces.length > 0 &&
            c.birth_year && // Check that birth_year is not empty
            !isNaN(parseInt(c.birth_year, 10)) // Check that it's a valid number
        );

        // 2. Separate clusters WITHOUT a valid birth year (and are not the 'unassigned' group).
        const clustersWithoutBirthYear = clusters.filter(c =>
            c.id !== 'cluster_unassigned' &&
            c.faces.length > 0 &&
            (!c.birth_year || isNaN(parseInt(c.birth_year, 10)))
        );

        // 3. Isolate the 'unassigned' faces from their specific cluster.
        const unassignedClusterFaces = clusters.find(c => c.id === 'cluster_unassigned')
            ?.faces.map(f => f.face_id || f.id) || [];

        // 4. Format the payloads for the API request.
        const identitiesPayload = clustersWithBirthYear.map(c => ({
            name: c.name,
            birth_year: c.birth_year,
            face_ids: c.faces.map(f => f.face_id || f.id)
        }));

        // This is the new, separate payload part for clusters missing a birth year
        const identitiesWithoutBirthYearPayload = clustersWithoutBirthYear.map(c => ({
            name: c.name,
            face_ids: c.faces.map(f => f.face_id || f.id)
        }));

        // --- FINAL PAYLOAD ---
        const payload = {
            // Clusters with birth dates
            identities: identitiesPayload,
            // The new, separate list of clusters without birth dates
            identities_without_birth_year: identitiesWithoutBirthYearPayload,
            // Just the faces from the 'unassigned' cluster
            unassigned_face_ids: unassignedClusterFaces,
            // The toggle state
            use_public_identities: usePublicIdentities
        };

        try {
            // IMPORTANT: You might want to use a different endpoint for this experimental version
            const response = await axios.post(`${API_URL}/custom-dating-experimental/`, payload);

            const resultsByFilename = response.data.dating_results_by_image;
            const formattedResults = selectedFiles.map(file => resultsByFilename[file.name] || { filename: file.name, faces: [], predicted_year: null });
            setDatingResultsForGallery(formattedResults);
            setMessage({ text: 'Experimental dating estimation complete!', severity: 'success' });
        } catch (error) {
            const errorMsg = error.response?.data?.detail || error.message || 'An unknown error occurred.';
            setMessage({ text: `Dating failed: ${errorMsg}`, severity: 'error' });
            setDatingResultsForGallery(null);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleFaceClick = (faceId) => setSelectedFaceId(prevId => (prevId === faceId ? null : faceId));
    const handleStageClick = () => setSelectedFaceId(null);
    const handleSlideChange = (index) => { setSelectedFaceId(null); setCurrentSlideIndex(index); };
    const selectedFaceData = datingResultsForGallery && selectedFaceId !== null && datingResultsForGallery[currentSlideIndex] ? datingResultsForGallery[currentSlideIndex].faces.find(f => f.id === selectedFaceId) : null;

    // Place this right before your return statement for clarity
    const isClusterView = clusters.length > 0 && selectedFiles.length > 0 && !datingResultsForGallery;

    if (isClusterView) {
        // --- RENDER PATH 1: THE VERSION THAT WORKS FOR CLUSTERS ---
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'background.default' }}>
                <AppBar position="static" color="default" sx={{ flexShrink: 0 }}>
                    <Toolbar>

                        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                            <img
                                src="logo.svg"
                                alt="University Logo"
                                style={{ height: '40px', marginRight: '16px' }}
                            />
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="h6" component="div" sx={{ lineHeight: 1.2 }}>
                                    Photo Dating by Facial Age Aggregation
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Machine Learning Group, Czech Technical University in Prague
                                </Typography>
                            </Box>
                        </Box>

                        {authState.token && (<Button variant="contained" component={Link} to="/library" sx={{ mr: 2 }}>My Library</Button>)}
                        <Button variant="contained" color="secondary" component={Link} to="/">Back to Standard Mode</Button>
                        <IconButton sx={{ ml: 1 }} onClick={themeMode.toggleThemeMode} color="inherit">
                            {theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                        </IconButton>
                        <AuthComponent token={authState.token} loggedInUser={authState.user?.username} handleLogin={handleLogin} handleLogout={handleLogout} username={username} setUsername={setUsername} password={password} setPassword={setPassword} />
                    </Toolbar>
                </AppBar>

                <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
                    <Box component="aside" sx={{ width: 320, p: 2, flexShrink: 0, overflowY: 'auto', bgcolor: 'background.paper' }}>
                        {message && <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
                        <CustomAppControlComponent onFilesAdded={onFilesAdded} handleProcess={handleProcess} handleReset={handleReset} isProcessing={isProcessing} hasFiles={selectedFiles.length > 0} usePublicIdentities={usePublicIdentities} setUsePublicIdentities={setUsePublicIdentities} useSavedIdentities={useSavedIdentities} setUseSavedIdentities={setUseSavedIdentities} handleCreateNewCluster={handleCreateNewCluster} handleSaveIdentities={handleSaveIdentities} isLoggedIn={!!authState.token} hasClusters={clusters.length > 0} handleRunDating={handleRunDating} handleRunDatingExperimental={handleRunDatingExperimental} />
                        {datingResultsForGallery && (<ProfileComponent selectedFace={selectedFaceData} onCancel={() => setSelectedFaceId(null)} />)}
                        <AboutComponent />
                        <LimitationsComponent />
                    </Box>

                    <Box component="main" ref={mainContentRef} sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1 }}>
                                <Grid container spacing={2}>
                                    {[...clusters].sort((a, b) => {
                                        if (a.id === 'cluster_unassigned') return 1;
                                        if (b.id === 'cluster_unassigned') return -1;
                                        return a.name.localeCompare(b.name, undefined, { numeric: true });
                                    }).map(cluster => (
                                        <Grid item key={cluster.id} xs={12} md={6} lg={4}>
                                            <FaceCluster cluster={cluster} onRemoveFace={handleRemoveFace} onBirthYearChange={handleBirthYearChange} onRemoveCluster={handleRemoveCluster} />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        </DragDropContext>
                    </Box>
                </Box>
            </Box>
        );

    } else {
        // --- RENDER PATH 2: THE VERSION THAT WORKS FOR THE GALLERY ---
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'background.default' }}>
                <AppBar position="static" color="default" sx={{ flexShrink: 0 }}>
                    <Toolbar>

                        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                            <img
                                src="logo.svg"
                                alt="University Logo"
                                style={{ height: '40px', marginRight: '16px' }}
                            />
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="h6" component="div" sx={{ lineHeight: 1.2 }}>
                                    Photo Dating by Facial Age Aggregation
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Machine Learning Group, Czech Technical University in Prague
                                </Typography>
                            </Box>
                        </Box>

                        {authState.token && (<Button variant="contained" component={Link} to="/library" sx={{ mr: 2 }}>My Library</Button>)}
                        <Button variant="contained" color="secondary" component={Link} to="/">Back to Standard Mode</Button>
                        <IconButton sx={{ ml: 1 }} onClick={themeMode.toggleThemeMode} color="inherit">
                            {theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                        </IconButton>
                        <AuthComponent token={authState.token} loggedInUser={authState.user?.username} handleLogin={handleLogin} handleLogout={handleLogout} username={username} setUsername={setUsername} password={password} setPassword={setPassword} />
                    </Toolbar>
                </AppBar>

                <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
                    <Box component="aside" sx={{ width: 320, p: 2, flexShrink: 0, overflowY: 'auto', bgcolor: 'background.paper' }}>
                        {message && <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
                        <CustomAppControlComponent onFilesAdded={onFilesAdded} handleProcess={handleProcess} handleReset={handleReset} isProcessing={isProcessing} hasFiles={selectedFiles.length > 0} usePublicIdentities={usePublicIdentities} setUsePublicIdentities={setUsePublicIdentities} useSavedIdentities={useSavedIdentities} setUseSavedIdentities={setUseSavedIdentities} handleCreateNewCluster={handleCreateNewCluster} handleSaveIdentities={handleSaveIdentities} isLoggedIn={!!authState.token} hasClusters={clusters.length > 0} handleRunDating={handleRunDating} handleRunDatingExperimental={handleRunDatingExperimental} />
                        {datingResultsForGallery && (<ProfileComponent selectedFace={selectedFaceData} onCancel={() => setSelectedFaceId(null)} />)}
                        <AboutComponent />
                        <LimitationsComponent />
                    </Box>

                    <Box component="main" sx={{ flexGrow: 1, p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto' }} ref={mainContentRef}>
                        {datingResultsForGallery ? (
                            <ImageGallery results={datingResultsForGallery} imageUrls={imageUrls} selectedFaceId={selectedFaceId} onFaceClick={handleFaceClick} onStageClick={handleStageClick} onSlideChange={handleSlideChange} onRemoveImage={handleRemoveImage} isProcessed={true} isProcessing={isProcessing} containerSize={containerSize} />
                        ) : selectedFiles.length === 0 ? (
                            <ImageDropzone onFilesAdded={onFilesAdded} />
                        ) : (
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography>{selectedFiles.length} image(s) ready.</Typography>
                                <Typography variant="body2" color="text.secondary">Click "Detect & Cluster Faces" to begin.</Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
                <FooterComponent />
            </Box>
        );
    }
};

export default CustomDatingView;
